import {useRef, useState} from "react";
import {useReducedMotion} from "framer-motion";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {motion} from "theme/tokens";
import type {ComposerMode} from "./composerMode";
import {COMPOSER_SLIDE_MS, composerPlacement, settleSlide, slideTo, type ComposerSlide} from "./composerSlide";

/**
 * The panel's turn between sending and asking for files as it happens on screen (`composerSlide`): which
 * composer is where, and the panel's height carried from the old composer's to the new one's while the
 * request composer slides, so nothing below the panel jumps. A thin adapter: the rules are
 * `composerSlide`'s; this measures, runs the height and ends the slide.
 *
 * `stageRef` is the box both composers sit in. Its height is set directly for the length of a slide and
 * handed back to the layout when it ends.
 */
export function useComposerSlide(mode: ComposerMode) {
	const stageRef = useRef<HTMLDivElement>(null);
	// The stage's height while at rest, kept current by a resize observer: a turn starts from it, and the
	// new composer is already in the stage by the time the turn's effect runs.
	const restingHeight = useRef(0);
	// Unknown (on the server, and before the first answer) counts as reduced: no slide is ever started on a
	// guess that motion is fine.
	const reducedMotion = useReducedMotion() !== false;
	const [slide, setSlide] = useState<ComposerSlide>({kind: "settled", mode});

	// A turn starts in the same render as the new mode, so the composer it turns to is shown, and can take
	// focus, from its first frame.
	const next = slideTo(slide, mode, reducedMotion);
	if (next !== slide) setSlide(next);

	useIsomorphicLayoutEffect(() => {
		const stage = stageRef.current;
		if (stage === null) return;
		restingHeight.current = stage.offsetHeight;
		// Without an observer the height is still re-read whenever a turn ends, below.
		if (typeof ResizeObserver !== "function") return;
		const observer = new ResizeObserver(() => {
			if (stage.style.height === "") restingHeight.current = stage.offsetHeight;
		});
		observer.observe(stage);
		return () => observer.disconnect();
	}, []);

	// Before paint: read the new composer's height with it in the panel's flow, put the old height back, and
	// let the browser carry it across while the composer slides; then hand the height back to the layout.
	useIsomorphicLayoutEffect(() => {
		const stage = stageRef.current;
		if (slide.kind !== "sliding" || stage === null) return;
		// A stage still carrying a height is mid-turn: the new turn starts from where that one has got to,
		// read before the running transition is stopped.
		const fromHeight = stage.style.height === "" ? restingHeight.current : stage.offsetHeight;
		stage.style.transition = "none";
		stage.style.height = "";
		const toHeight = stage.offsetHeight;
		stage.style.height = `${fromHeight}px`;
		void stage.offsetHeight;
		stage.style.transition = `height ${COMPOSER_SLIDE_MS}ms ${motion.easeOut}`;
		stage.style.height = `${toHeight}px`;
		const timer = window.setTimeout(() => {
			stage.style.transition = "";
			stage.style.height = "";
			restingHeight.current = stage.offsetHeight;
			setSlide(settleSlide);
		}, COMPOSER_SLIDE_MS);
		return () => window.clearTimeout(timer);
	}, [slide]);

	return {stageRef, sliding: next.kind === "sliding", placement: composerPlacement(next)};
}
